import { useState, useEffect } from "react";

/**
 * SSR-safe hook to detect if user prefers reduced motion
 * Used to optimize animations for accessibility and performance
 *
 * HYDRATION FIX: Returns `false` during SSR and initial hydration,
 * then updates to actual preference after mount. This prevents hydration
 * mismatch by ensuring server and client render identically on first pass.
 *
 * @returns boolean - true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  // Start with false to match SSR output (no window available on server)
  // This ensures hydration consistency: server=false, initial client=false
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    // Mark as mounted - now safe to read window
    setHasMounted(true);

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    // Set actual value after mount
    handleChange();

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // During SSR or before mount, return false (consistent with server)
  // After mount, return actual preference
  return hasMounted ? prefersReducedMotion : false;
}